import * as excel from '~/adapters/excel';
import { defaultLang } from '~/config/settings/app.settings';
import { typeNames } from '~/config/settings/survey_element.settings';
import { Question, ValidateSurveyMetadataParams } from '~/interfaces/survey.interface';

const ANSWER_PREFIX = 'answer:::';
const COMMENT_PREFIX = 'comment:::';
const ANSWER_PATTERN = /(answer:::)([^:])/gi;
const COMMENT_PATTERN = /(comment:::)([^:])/gi;
const LINE_BREAK_PATTERN = /(\r\n|\n|\r)/gm;
const EMPLOYEE_ID_TITLE = 'Employee Id';
const COMPLETED_SURVEY_TITLE = 'Completed Survey';
const INVITATION_TIME_TITLE = 'Invitation Time';
// Optional column: when present, the original response date is preserved on migrated
// comments/responses. Allowed (not flagged as unsupported) but never required.
const SUBMISSION_DATE_TITLE = 'Submission Date';

const validateRequiredTitles = (headerTitles) => {
  const headerTitlesSet = new Set(headerTitles);
  const hasEmployeeId = headerTitlesSet.has(EMPLOYEE_ID_TITLE);
  const hasCompletedSurvey = headerTitlesSet.has(COMPLETED_SURVEY_TITLE);
  const hasInvitationTime = headerTitlesSet.has(INVITATION_TIME_TITLE);

  return { hasEmployeeId, hasCompletedSurvey, hasInvitationTime };
};

const validateUnsupportedTitles = (unsupportedHeaders, attributeNames: string[] = []) => {
  const unsupportedEmployeeId = unsupportedHeaders.find((title) => {
    return title.search(/^employee id/gi) > -1;
  });
  const unsupportedCompletedSurvey = unsupportedHeaders.find((title) => {
    return title.search(/^completed survey/gi) > -1;
  });
  const unsupportedTitles = unsupportedHeaders.filter((title) => {
    const isUnsupportedEmployeeId = title.search(/^employee id/gi) > -1;
    const isUnsupportedCompletedSurvey = title.search(/^completed survey/gi) > -1;
    const isValidAttribute = attributeNames.some((name) => name && name.toLowerCase() === title.toLowerCase());

    return !isUnsupportedEmployeeId && !isUnsupportedCompletedSurvey && !isValidAttribute;
  });

  return {
    unsupportedEmployeeId,
    unsupportedCompletedSurvey,
    unsupportedTitles,
  };
};

const getUnsupportedHeaders = (headers) => {
  const unsupportedHeaders = headers.filter((title) => {
    const isComment = title.search(COMMENT_PATTERN) !== -1;
    const isAnswered = title.search(ANSWER_PATTERN) !== -1;
    const isKnownMetadataHeader =
      title === EMPLOYEE_ID_TITLE ||
      title === COMPLETED_SURVEY_TITLE ||
      title === INVITATION_TIME_TITLE ||
      title === SUBMISSION_DATE_TITLE;

    return !isComment && !isAnswered && !isKnownMetadataHeader;
  });

  return unsupportedHeaders;
};

const checkAnswerIsValidRating = (fileData, answerHeaders) => {
  let result = true;
  fileData.every((fd) => {
    const answer = fd[answerHeaders];

    if (answer) {
      if ((answer.toLowerCase() !== 'yes' || answer.toLowerCase() !== 'no') && (isNaN(answer) || Number(answer) < 0)) {
        result = false;
        return false;
      }
    }
    return true;
  });
  return result;
};

const getQuestions = (fileData, answerHeaders, commentHeaders) => {
  const searchableAnswersHeaders = answerHeaders.toString();
  const searchableAnswersComments = commentHeaders.toString();
  const allQuestionsHeader = Array.from(new Set([...answerHeaders, ...commentHeaders]));
  const cleanQuestionHeaders = allQuestionsHeader.map((title) => {
    return title.replace(ANSWER_PATTERN, '$2').replace(COMMENT_PATTERN, '$2');
  });

  const questionsDict: { [title: string]: Question } = {};
  const duplicatedQuestionsDict = {};

  cleanQuestionHeaders.forEach((title) => {
    const answerHeader = ANSWER_PREFIX + title;
    const uniqueAnswers = excel.getUniqueQuestionAnswers(fileData, answerHeader);
    const hasValidRatingAnswer = checkAnswerIsValidRating(fileData, answerHeader);

    if (questionsDict[title]) return;

    const question = {
      title,
      hasAnswer: searchableAnswersHeaders.indexOf(ANSWER_PREFIX + title) > -1,
      hasComment: searchableAnswersComments.indexOf(COMMENT_PREFIX + title) > -1,
      uniqueAnswers,
      hasValidRatingAnswer,
    };
    const isDuplicate = title.search(/^.*(_\d+)$/) > -1;

    if (isDuplicate) {
      const fixedTitle = title.replace(/^(.*)(_\d+)$/, '$1');
      duplicatedQuestionsDict[fixedTitle] = {
        ...question,
        title: fixedTitle,
      };
    } else {
      questionsDict[title] = question;
    }
  });

  return { questionsDict, duplicatedQuestionsDict };
};

const getEmployeeIdWithCompletedSurveyAnalysis = (fileData, headers, answerHeaders) => {
  const hasEmployeeId = headers.includes(EMPLOYEE_ID_TITLE);
  if (!hasEmployeeId) return {};
  const hasCompletedSurvey = headers.includes(COMPLETED_SURVEY_TITLE);
  const employeeIdsWithCompletedSurveyAnalysis = {};
  fileData.forEach((fd) => {
    const employeeId = fd[EMPLOYEE_ID_TITLE];
    if (!hasCompletedSurvey) {
      employeeIdsWithCompletedSurveyAnalysis[employeeId] = false;
    } else {
      const NotCompletedSurvey = fd[COMPLETED_SURVEY_TITLE].toLowerCase() === 'no';
      const hasAnswer = answerHeaders.some((header) => {
        return fd[header] && fd[header].trim() !== '';
      });

      employeeIdsWithCompletedSurveyAnalysis[employeeId] = NotCompletedSurvey && hasAnswer;
    }
  });

  return employeeIdsWithCompletedSurveyAnalysis;
};

const getMigrationSurveyData = (jsonData) => {
  const headers = excel.getHeadersTitles(jsonData).map((header) => header.replace(LINE_BREAK_PATTERN, ''));
  const questionsCommentHeaders = headers.filter((title) => title.search(COMMENT_PATTERN) !== -1);
  const questionAnswerHeaders = headers.filter((title) => title.search(ANSWER_PATTERN) !== -1);
  const unsupportedHeaders = getUnsupportedHeaders(headers);

  const { questionsDict, duplicatedQuestionsDict } = getQuestions(
    jsonData,
    questionAnswerHeaders,
    questionsCommentHeaders,
  );

  const employeeIdsWithCompletedSurveyAnalysis = getEmployeeIdWithCompletedSurveyAnalysis(
    jsonData,
    headers,
    questionAnswerHeaders,
  );

  return {
    headers,
    commentHeaders: questionsCommentHeaders,
    answerHeaders: questionAnswerHeaders,
    unsupportedHeaders,
    questionsDict,
    duplicatedQuestionsDict,
    employeeIdsWithAnalysis: employeeIdsWithCompletedSurveyAnalysis,
  };
};

const validateExisingQuestions = (fileQuestionsTitles, surveyQuestionsTitles) => {
  const nonExistingQuestions: string[] = [];
  fileQuestionsTitles.forEach((headerTitle) => {
    if (surveyQuestionsTitles.indexOf(headerTitle) < 0) {
      nonExistingQuestions.push(headerTitle);
    }
  });

  return nonExistingQuestions;
};

const validateOptionsExist = (optionsInExcel, optionsInQuestion) => {
  const missingOptions: string[] = [];
  optionsInExcel.forEach((option) => {
    if (optionsInQuestion.indexOf(option) < 0) {
      missingOptions.push(option);
    }
  });

  return missingOptions;
};

const validateEmployeeIdsAndCompleted = (
  employeeIdsWithAnalysis: { [employeeId: string]: boolean },
  employeeIds: string[],
) => {
  const invalidEmployeeIds: string[] = [];
  const employeesWithWrongCompletedSurvey: string[] = [];
  Object.keys(employeeIdsWithAnalysis).forEach((employeeId) => {
    if (!employeeIds.includes(employeeId)) {
      invalidEmployeeIds.push(employeeId);
    } else {
      if (employeeIdsWithAnalysis[employeeId]) {
        employeesWithWrongCompletedSurvey.push(employeeId);
      }
    }
  });

  return { invalidEmployeeIds, employeesWithWrongCompletedSurvey };
};

const validateSurveyMetadata = ({
  surveyData,
  attributeNames,
  surveyElements,
  employeeIds,
  companyEmployeeIdsInSurvey,
}: ValidateSurveyMetadataParams) => {
  const fileQuestionsTitles = Object.values(surveyData.questionsDict);
  const surveyQuestionTitles = surveyElements.map((question) => question.title.replace(LINE_BREAK_PATTERN, ''));

  const { hasEmployeeId, hasCompletedSurvey, hasInvitationTime } = validateRequiredTitles(surveyData.headers);
  const { unsupportedEmployeeId, unsupportedCompletedSurvey, unsupportedTitles } = validateUnsupportedTitles(
    surveyData.unsupportedHeaders,
    attributeNames,
  );
  const nonExistingQuestions = validateExisingQuestions(Object.keys(surveyData.questionsDict), surveyQuestionTitles);

  const commentsWithoutAnswer = fileQuestionsTitles
    .filter(({ hasAnswer, hasComment }) => !hasAnswer && !!hasComment)
    .map(({ title }) => title);
  const duplicatedQuestions = Object.keys(surveyData.duplicatedQuestionsDict);

  const { invalidEmployeeIds, employeesWithWrongCompletedSurvey } = validateEmployeeIdsAndCompleted(
    surveyData.employeeIdsWithAnalysis,
    employeeIds,
  );

  const invalidRatingAnswers: string[] = [];
  const scaleQuestionTypeIds = [2, 4, 7];
  const scaleQuestions = surveyElements.filter((se) => {
    return scaleQuestionTypeIds.includes(se.typeId);
  });
  scaleQuestions.forEach((scaleQuestion) => {
    // Missing questions or questions without answers are not invalid answers
    const question = surveyData.questionsDict?.[scaleQuestion.title];
    if (question && 'hasValidRatingAnswer' in question && !question.hasValidRatingAnswer) {
      invalidRatingAnswers.push(scaleQuestion.title);
    }
  });

  const missingMultipleChoiceOptions = {};
  const multipleChoiceQuestions = surveyElements.filter((se) => {
    return se.typeName === typeNames.MULTIPLE;
  });

  multipleChoiceQuestions.forEach((mcQuestion) => {
    const questionFromExcel = surveyData?.questionsDict[mcQuestion.title.replace(LINE_BREAK_PATTERN, '')];

    if (questionFromExcel) {
      const excelOptions = questionFromExcel.uniqueAnswers;

      if (mcQuestion.translations[defaultLang]) {
        // Only support migration based on English questions
        const englishQuestionOptions = Object.values(mcQuestion.translations[defaultLang]);
        const optionsNotInQuestion = validateOptionsExist(excelOptions, englishQuestionOptions);

        if (optionsNotInQuestion.length) {
          missingMultipleChoiceOptions[mcQuestion.title] = optionsNotInQuestion;
        }
      }
    }
  });

  const importedCompanyEmployeeIds = Object.keys(surveyData.employeeIdsWithAnalysis);
  const rejoinedEmployeeIds: string[] = [];
  for (const companyEmployeeId of importedCompanyEmployeeIds) {
    if (companyEmployeeIdsInSurvey.includes(companyEmployeeId)) {
      rejoinedEmployeeIds.push(companyEmployeeId);
    }
  }

  return {
    ...(!hasEmployeeId ? { noEmployeeIdColumn: true } : null),
    ...(!hasCompletedSurvey ? { noCompletedSurveyColumn: true } : null),
    ...(!hasInvitationTime ? { noInvitationTimeColumn: true } : null),
    ...(unsupportedEmployeeId ? { unsupportedEmployeeId } : null),
    ...(unsupportedCompletedSurvey ? { unsupportedCompletedSurvey } : null),
    ...(unsupportedTitles.length ? { unsupportedTitles } : null),
    ...(commentsWithoutAnswer.length ? { commentsWithoutAnswer } : null),
    ...(duplicatedQuestions.length ? { duplicatedQuestions } : null),
    ...(nonExistingQuestions.length ? { nonExistingQuestions } : null),
    ...(Object.values(missingMultipleChoiceOptions).length ? { missingMultipleChoiceOptions } : null),
    ...(invalidRatingAnswers.length ? { invalidRatingAnswers } : null),
    ...(invalidEmployeeIds.length ? { invalidEmployeeIds } : null),
    ...(employeesWithWrongCompletedSurvey.length ? { employeesWithWrongCompletedSurvey } : null),
    ...(rejoinedEmployeeIds.length ? { rejoinedEmployeeIds } : null),
  };
};

export { getMigrationSurveyData, validateSurveyMetadata };
